import React, {
    createContext,
    useState,
    useEffect,
    FunctionComponent,
    PropsWithChildren,
    useContext,
} from "react";
import settings from './settings'

const { REACT_APP_BACKEND_URL } = settings;

type AuthStatus =
    | "pending"
    | "authenticated"
    | "not-authenticated"
    | "unidentified-user"
    | "error";

export const AuthStatus = {
    PENDING: "pending",
    AUTHENTICATED: "authenticated",
    NOT_AUTHENTICATED: "not-authenticated",
    UNIDENTIFIED_USER: "unidentified-user",
    ERROR: "error",
} as const;

interface AuthContextType {
    authStatus: AuthStatus;
    loginWithMicrosoft: () => void;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error(
            "useAuthContext must be used within an AuthProvider"
        );
    }
    return context;
};

const AuthProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [authStatus, setAuthStatus] = useState<AuthStatus>("pending");

    const getQueryParam = (param: string): string | null => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    };

    useEffect(() => {
        const token = getQueryParam('token');
        if (token) {
            localStorage.setItem('access_token', token);
            window.history.replaceState({}, document.title, "/");  
        }

        const fetchAuthenticationStatus = async () => {
            setAuthStatus(AuthStatus.PENDING);
            const storedToken = localStorage.getItem('access_token');
            
            if (storedToken) {
                const response = await fetch(`${REACT_APP_BACKEND_URL}/auth/token/validate`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${storedToken}`,
                    },
                });
                if (response.ok) {
                    setAuthStatus(AuthStatus.AUTHENTICATED);
                } else {
                    setAuthStatus(AuthStatus.NOT_AUTHENTICATED);
                }
            } else {
                setAuthStatus(AuthStatus.NOT_AUTHENTICATED);
            }
        };
        fetchAuthenticationStatus();
    }, []);  

    const loginWithMicrosoft = () => {
        window.location.href = `${REACT_APP_BACKEND_URL}/auth/login`;
    };

    const logout = () => {
        localStorage.removeItem('access_token');
        window.location.href = `${REACT_APP_BACKEND_URL}/auth/logout`;
    };

    return (
        <AuthContext.Provider value={{ authStatus, loginWithMicrosoft, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
